// The door, from the phone's side.
//
// A card used to carry a seat and that was the end of it: the token in ?t= went
// straight to the socket. A hall can now hand out a code for a whole block, or
// one code for the house, and neither carries a seat — so between scanning and
// playing there is a conversation.
//
// THE PHONE DECIDES NOTHING. It sends the code it scanned to /entry/resolve,
// renders whatever question comes back, sends the answer to /entry/claim,
// and boots with the token it is given. Which zones exist, whether a seat is
// asked for, what a full house does — all of it is the hall's, tonight, and a
// phone that has been in a pocket since the spring must not act on what was true
// then. This file knows how to ask and how to draw; it does not know the rules.
//
// The ordinary case still costs nothing an audience will feel: a seat card
// resolves with nothing to ask and boots. It does go through the door — that is
// how a house limit can count it — but if the door cannot be reached the phone
// falls back to the token it already has. A card on a chair worked before any of
// this existed and has to keep working when the hub is having a bad night.
//
// THAT FALLBACK IS ONLY EVER RIGHT FOR A SEAT CARD, and getting this wrong cost
// a production evening. A block or house code carries no seat; the socket
// refuses it, correctly, and the client then retries forever — so a phone that
// had done everything right sat on "Reconnecting" while the real fault was an
// edge that answered the door with a 404 page. Falling back on a code that
// cannot open a socket turns a one-line routing mistake into a symptom nobody
// can read. Now it says so instead.

import { t, type StringKey } from "./i18n";
// The scanner is one of this module's answers now: a hall that prints cards
// tells a phone with none to go and read one. No cycle — gate.ts takes i18n and
// jsqr and nothing else.
import { mountSeatGate } from "./gate";
// The same id the socket sends. Imported from js/ rather than minted here so
// that the door and the socket cannot disagree about which phone this is.
import { deviceId } from "../js/device.js";

export type Admission = {
  token: string;
  /** A spectator is connected and heard by nobody. It still draws its own touches. */
  spectator: boolean;
};

/**
 * What the phone should call itself on screen, as the hall answered it.
 *
 * Kept in sessionStorage rather than in a variable because the status bar is
 * drawn by the app, long after the door is gone, and because a reload should not
 * flash "A1" before the door answers again.
 */
export type Display = "seat" | "zone" | "live";

export function displayMode(): Display {
  try {
    const v = sessionStorage.getItem("cs_display");
    return v === "zone" || v === "live" ? v : "seat";
  } catch { return "seat"; }
}

/**
 * The name the hall gave this phone, when it gave it one at all.
 *
 * Almost every phone can work its own name out from the two bytes of its token
 * and the zone names on the page, and does — see main.ts. This exists for the
 * one that cannot: a SILENT spectator, whose token says zone 254, which is a
 * sentinel and not a block, and which would render as a stray Latin letter.
 *
 * The hall answers it instead, and the answer is deliberately the same shape as
 * everybody else's. Empty means "nothing to say here, work it out yourself",
 * which is what every player and every told spectator gets.
 */
export function displayLabel(): string {
  try { return sessionStorage.getItem("cs_label") ?? ""; } catch { return ""; }
}

type Zone = { id: string; label: string };
type Resolved = {
  ok: boolean;
  url?: string;
  kind?: string;
  ask?: "zone" | "seat" | null;
  zone?: string;
  zones?: Zone[];
  seatRange?: { from: number; to: number };
  label?: string;
  reason?: string;
};
type Claimed = {
  ok: boolean;
  url?: string;
  token?: string;
  role?: "player" | "spectator";
  display?: Display;
  label?: string;
  told?: boolean;
  reason?: string;
  seatRange?: { from: number; to: number };
};

// A phone with no code used to be asked a question of its own here — "is the
// hall admitting anyone?" — so that main.ts could choose between the scanner and
// the venue's link before anything else happened. It does not need asking any
// more: admitPhone() takes the codeless phone through the same door as every
// other one, and the door answers all three cases itself — `closed` sends it to
// the venue's link, `needcode` to the scanner, and under one code for the house
// it simply lets it in, because there the address is the card.

/** The code this phone arrived with, from the address or from the last load. */
export function scannedToken(): string {
  const fromUrl = new URLSearchParams(location.search).get("t");
  if (fromUrl) return fromUrl;
  try { return sessionStorage.getItem("cs_token") ?? ""; } catch { return ""; }
}

/**
 * Tonight's code, from the address or from the last load. Same shape as the one
 * above and for the same reason, but the fallback is not a convenience here —
 * it is the only thing that makes a reload work.
 *
 * ws-client.js does `history.replaceState(null, "", location.pathname)` when it
 * is imported, which wipes the WHOLE query and not just the token it came for.
 * So by the second load the address has no code in it, and a phone that has been
 * playing for an hour would be sent back to the scanner by its own screen
 * locking and unlocking. sessionStorage is where the code actually lives from
 * the first admission onward; the address is only ever the way it arrives.
 *
 * Which also gives the retirement rule its shape: retiring a code stops new
 * arrivals and survives a reload only for as long as the tab does. Nobody
 * playing is dropped, because the door is not asked again while a socket is up.
 */
export function arrivedCode(): string {
  // `s` is what cards carry now; `show` is what every card printed before this
  // change carries, and those are on chairs in a building. Both are read, for as
  // long as any of them are still in the room — the parameter got shorter to buy
  // QR modules, and a card that stops working is a worse trade than three
  // characters. Newest name first, so a card carrying both cannot be ambiguous.
  const q = new URLSearchParams(location.search);
  const fromUrl = q.get("s") ?? q.get("show");
  if (fromUrl) return fromUrl.toUpperCase();
  try { return sessionStorage.getItem("cs_show") ?? ""; } catch { return ""; }
}

function remember(token: string, display: Display = "seat", label = "") {
  // What this phone is, in the hall's own terms. Written before the token so
  // that anything reading both can never see a new seat with an old label.
  //
  // Only on a FRESH ARRIVAL, and that is the whole subtlety. A code in the
  // address is a card somebody just scanned, and what the door says about it is
  // the truth. No code in the address means this is the same phone loading the
  // same page again, carrying the seat token it was admitted with — and that
  // token cannot describe how it got in. A block card names its block; the seat
  // it was handed out of the pool names nothing, and the door, asked a second
  // time, can only see the pool. So the phone keeps what it already learned
  // rather than being told something smaller.
  const arrived = new URLSearchParams(location.search).has("t");
  try {
    if (arrived || !sessionStorage.getItem("cs_display")) {
      sessionStorage.setItem("cs_display", display);
      // Under exactly the same rule, and it has to be: the two describe one
      // answer. A label kept next to a display from a different admission would
      // put last card's name on this card's seat.
      //
      // The name is also the one thing here the door cannot reproduce. A silent
      // spectator that reloads comes back holding a spectator token, and the
      // hall answers a reload without deciding anything again — so if this were
      // dropped on reload the name would be lost and the screen would fall back
      // to saying "Live", which is precisely what the silent form exists to
      // avoid. Kept for the life of the tab, like the token it goes with.
      if (label) sessionStorage.setItem("cs_label", label);
      else sessionStorage.removeItem("cs_label");
    }
  } catch { /* private mode */ }

  // The ws client reads this at import time and it is imported lazily, well
  // after this runs — so writing it here is what makes the admitted token the
  // one the socket dials with, rather than the code that was scanned.
  try { sessionStorage.setItem("cs_token", token); } catch { /* private mode */ }

  // …and tonight's code with it. Unconditionally, unlike cs_display above: this
  // one does not describe how the phone got in, it is the thing it has to
  // present next time, and the address is about to stop carrying it.
  try {
    const code = arrivedCode();
    if (code) sessionStorage.setItem("cs_show", code);
  } catch { /* private mode */ }

  // …and the address has to let go of the scanned one. ws-client.js reads ?t=
  // FIRST and writes it over sessionStorage, so leaving it there would hand the
  // socket the block code this phone just exchanged — which the socket refuses,
  // and the phone would sit on a black screen having done everything right.
  //
  // replaceState, not a reload: this is the same page, one step further along,
  // and the back button should not lead to a code that has already been spent.
  try {
    const url = new URL(location.href);
    if (url.searchParams.has("t")) {
      url.searchParams.delete("t");
      history.replaceState(null, "", url.pathname + (url.search || "") + url.hash);
    }
  } catch { /* an address we cannot rewrite is one sessionStorage still wins on reload */ }
}

const post = async (path: string, body: unknown): Promise<Claimed & Resolved> => {
  const r = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  // A door that answers with a page rather than JSON is a door that is not
  // there: an edge with no route for it, a captive portal, a proxy error. It
  // throws here and is handled as unreachable, which is what it is — the one
  // thing it must not do is look like an answer.
  return (await r.json()) as Claimed & Resolved;
};

const nap = (msec: number) => new Promise((r) => setTimeout(r, msec));

/** How long a phone waits at a busy door before it stops waiting and says so. */
const QUEUE_MS = 60_000;

/**
 * post(), except that a request the door SHEDS waits its turn instead of failing.
 *
 * This is the difference between a limiter that is a queue and a limiter that is
 * a door slam. The door buckets per client address, and a hall on mobile data
 * arrives through a handful of carrier NATs — so at doors-open a few hundred
 * phones can be shed purely for sharing an address with each other, while the
 * hub itself sits at one percent of a core. Every one of them was previously
 * told "that code is not from tonight", with no button, which is both wrong and
 * a dead end: measured, five addresses and two thousand phones in a minute puts
 * about four hundred people on that screen.
 *
 * Retrying costs the server nothing it was not already paying — those requests
 * are refused before any work — and the bucket refills while the phone waits.
 *
 * THE JITTER IS THE POINT, not decoration. The whole hall was shed by the same
 * bucket in the same instant, so a fixed delay brings all of them back in the
 * same instant to be shed again, and the queue never drains. The server's own
 * Retry-After is a constant five seconds and has exactly that flaw, which is why
 * it is deliberately not used.
 */
async function queued(path: string, body: unknown): Promise<Claimed & Resolved> {
  const until = Date.now() + QUEUE_MS;
  let wait = 1200;
  for (;;) {
    const r = await post(path, body);
    // Anything else — including a refusal — is an answer and belongs to the
    // caller. Only "the door is shedding right now" is worth waiting out.
    if (r.reason !== "busy") return r;
    if (Date.now() >= until) return r;
    say("working", t("entryBusy"));
    await nap(Math.round(wait * (0.6 + Math.random() * 0.8)));
    wait = Math.min(Math.round(wait * 1.6), 8000);
  }
}

/**
 * Can this code open a socket on its own?
 *
 * Two bytes, no secret needed — the same local read the ws client does to show a
 * seat number. A seat card carries its own seat and works with no door at all; a
 * block code (seat 0), a house code (zone 255) and a spectator token (zone 254)
 * do not, and never will.
 */
function carriesItsOwnSeat(token: string): boolean {
  try {
    const raw = atob(token.replace(/-/g, "+").replace(/_/g, "/"));
    if (raw.length < 2) return false;
    const zone = raw.charCodeAt(0) & 0xff;
    const seat = raw.charCodeAt(1) & 0xff;
    return seat !== 0 && zone < 254;
  } catch { return false; }
}

// ── the panels ───────────────────────────────────────────────────────────────
// One element, four faces. They are built here rather than in index.html because
// three of the four are never shown to most people, and markup for a question
// nobody is asked is markup that goes stale unnoticed.

let root: HTMLElement | null = null;

function panel(): HTMLElement {
  if (root) return root;
  root = document.createElement("div");
  root.id = "entry-gate";
  document.body.appendChild(root);
  // The app's own chrome would be furniture over a question.
  document.getElementById("loading-overlay")?.classList.add("hidden");
  document.getElementById("status-bar")?.classList.add("hidden");
  document.getElementById("hud-buttons")?.classList.add("hidden");
  return root;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) =>
  ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));

function say(kind: "working" | "message", title: string, sub = "") {
  const el = panel();
  el.className = "entry-" + kind;
  el.innerHTML = `<div class="entry-card">
      <h1>${esc(title)}</h1>
      ${sub ? `<p>${esc(sub)}</p>` : ""}
    </div>`;
}

/** A dead end with a way out of it. */
function stop(titleKey: StringKey, subKey: StringKey, retry: boolean) {
  const el = panel();
  el.className = "entry-message";
  el.innerHTML = `<div class="entry-card">
      <h1>${esc(t(titleKey))}</h1>
      <p>${esc(t(subKey))}</p>
      ${retry ? `<button type="button" class="entry-go" id="entry-retry">${esc(t("entryRetry"))}</button>` : ""}
    </div>`;
  document.getElementById("entry-retry")?.addEventListener("click", () => location.reload());
}

function askZone(zones: Zone[]): Promise<string> {
  return new Promise((resolve) => {
    const el = panel();
    el.className = "entry-ask";
    el.innerHTML = `<div class="entry-card">
        <h1>${esc(t("entryZoneTitle"))}</h1>
        <p>${esc(t("entryZoneSub"))}</p>
        <div class="entry-zones">${zones.map((z) => `
          <button type="button" data-zone="${esc(z.id)}">
            <span class="ez-id">${esc(z.id)}</span>
            ${z.label ? `<span class="ez-label">${esc(z.label)}</span>` : ""}
          </button>`).join("")}</div>
      </div>`;
    el.querySelector(".entry-zones")?.addEventListener("click", (ev) => {
      const b = (ev.target as HTMLElement).closest("button[data-zone]") as HTMLElement | null;
      if (b) resolve(b.dataset.zone ?? "");
    });
  });
}

function askSeat(range: { from: number; to: number }, label: string, problem = ""): Promise<number> {
  return new Promise((resolve) => {
    const el = panel();
    el.className = "entry-ask";
    const hint = t("entrySeatRange").replace("{from}", String(range.from)).replace("{to}", String(range.to));
    el.innerHTML = `<div class="entry-card">
        <h1>${esc(t("entrySeatTitle"))}</h1>
        <p>${esc(label || t("entrySeatSub"))}</p>
        ${problem ? `<p class="entry-problem">${esc(problem)}</p>` : ""}
        <input id="entry-seat" type="number" inputmode="numeric" autocomplete="off"
               min="${range.from}" max="${range.to}" placeholder="${range.from}–${range.to}"/>
        <p class="entry-hint">${esc(hint)}</p>
        <button type="button" class="entry-go" id="entry-seat-go">${esc(t("entrySeatGo"))}</button>
      </div>`;
    const input = document.getElementById("entry-seat") as HTMLInputElement;
    const go = () => {
      const n = parseInt(input.value, 10);
      // Only the shape is checked here. Whether that seat is free, and whether
      // it is in this block at all, is the server's answer — asking twice in two
      // places is how the two come to disagree.
      if (Number.isInteger(n)) resolve(n);
      else input.focus();
    };
    document.getElementById("entry-seat-go")?.addEventListener("click", go);
    input.addEventListener("keydown", (ev) => { if (ev.key === "Enter") go(); });
    // Not autofocused: on iOS that opens the keyboard over the question the
    // person has not read yet.
  });
}

/** The spectator's notice, when the hall chose to tell them. */
function tellThem(): Promise<void> {
  return new Promise((resolve) => {
    const el = panel();
    el.className = "entry-message";
    el.innerHTML = `<div class="entry-card">
        <h1>${esc(t("entryWatchTitle"))}</h1>
        <p>${esc(t("entryWatchSub"))}</p>
        <button type="button" class="entry-go" id="entry-watch-go">${esc(t("entryWatchGo"))}</button>
      </div>`;
    document.getElementById("entry-watch-go")?.addEventListener("click", () => resolve());
  });
}

function done() {
  root?.remove();
  root = null;
  document.getElementById("loading-overlay")?.classList.remove("hidden");
  document.getElementById("status-bar")?.classList.remove("hidden");
  document.getElementById("hud-buttons")?.classList.remove("hidden");
}

/**
 * Take the panel away and give the page to somebody else.
 *
 * done()'s quieter half: the app's chrome stays hidden, because what follows is
 * the scanner, which hides all three again the moment it mounts. Unhiding them
 * here would be one frame of a status bar over a camera.
 */
function dismiss() {
  root?.remove();
  root = null;
}

/**
 * Get this phone a seat, asking whatever the hall asks.
 *
 * Resolves with an admission, or never — a dead end (a full house, a code from
 * another night) leaves its own screen up, because there is nothing to boot into
 * and a black screen behind a message is worse than the message alone.
 */
export async function admitPhone(): Promise<Admission> {
  const scanned = scannedToken();
  // Sent on BOTH calls, not just the first. The door is stateless between them —
  // there is no session here, only two posts — so the second one has to present
  // the same code as the first or it is a different phone as far as the door
  // knows.
  const show = arrivedCode();
  say("working", t("entryWorking"));

  let r: Resolved;
  try {
    r = await queued("/entry/resolve", { t: scanned, show });
  } catch {
    return await unreachable(scanned);
  }

  if (!r.ok) {
    // The hall is not letting anybody in. Not an error and not a dead end — the
    // person is sent where the venue wants them, which is the whole point of the
    // setting. replace(), so the back button does not bring them round again.
    if (r.reason === "closed") return await leave(r.url);
    // This hall wants a card and this phone has not shown one. Not an error
    // either: it is somebody who typed the address in a hall that prints codes,
    // and the scanner is the screen they came for. The message screens below are
    // for a code that was shown and turned out to be no good.
    if (r.reason === "needcode") { dismiss(); mountSeatGate(); return await never(); }
    // Still shedding after a minute of waiting. A real message this time, with
    // the button — the queue did not drain and pretending otherwise would leave
    // somebody staring at "one moment" for the whole first act.
    if (r.reason === "busy") { stop("entryBusyTitle", "entryBusySub", true); return await never(); }
    if (r.reason === "notthisplan") stop("entryFullTitle", "entryStale", false);
    else if (r.reason === "unknownzone") stop("entryFullTitle", "entryGone", false);
    else stop("entryFullTitle", "entryStale", false);
    return await never();
  }

  // The phone's own id goes with the request. The door used to have nothing that
  // identified a phone — its own rate limiter says so, and limits by address
  // instead, because a hall arrives through a handful of carrier NATs. This is
  // the exception: not a credential and not trusted for anything, just enough
  // for the hall to notice that the seat it is about to hand out would be the
  // second one for a phone that already has one.
  //
  // Without it the socket rule alone is half a fix. A second tab would still be
  // ADMITTED — a whole second seat, counted against the house for a minute —
  // and only then closed on arrival, so a person opening tabs could still empty
  // a small hall without ever playing a note.
  const body: Record<string, unknown> = { t: scanned, show, d: deviceId() };

  if (r.ask === "zone") body.zone = await askZone(r.zones ?? []);

  let range = r.seatRange;
  let problem = "";
  for (;;) {
    if (r.ask === "seat" && range) body.seat = await askSeat(range, r.label ?? "", problem);
    else if (r.ask === "seat") { stop("entryFullTitle", "entryClosed", true); return await never(); }

    let c: Claimed;
    try {
      say("working", t("entryWorking"));
      c = await queued("/entry/claim", body);
    } catch {
      return await unreachable(scanned);
    }

    if (c.ok && c.token) {
      remember(c.token, c.display ?? "seat", c.label ?? "");
      // Told, and only when the hall chose to tell them. The silent form is the
      // whole point of the other setting: they play to themselves and never find
      // out, which is a decision the operator made about the night.
      if (c.role === "spectator" && c.told) await tellThem();
      done();
      return { token: c.token, spectator: c.role === "spectator" };
    }

    // The two answers worth asking again about. Everything else is a dead end.
    if (c.reason === "badseat" || c.reason === "taken") {
      range = c.seatRange ?? range;
      problem = c.reason === "taken"
        ? t("entrySeatTaken")
        : t("entrySeatBad").replace("{from}", String(range?.from ?? "")).replace("{to}", String(range?.to ?? ""));
      r = { ...r, ask: "seat" };
      continue;
    }
    if (c.reason === "busy")        { stop("entryBusyTitle", "entryBusySub", true); return await never(); }
    if (c.reason === "closed")      return await leave(c.url);
    if (c.reason === "full")        { stop("entryFullTitle", "entryFullSub", true); return await never(); }
    if (c.reason === "unavailable") { stop("entryFullTitle", "entryOffline", true); return await never(); }
    if (c.reason === "badzone")     { stop("entryFullTitle", "entryGone", false); return await never(); }
    stop("entryFullTitle", "entryStale", false);
    return await never();
  }
}

/**
 * The door did not answer at all.
 *
 * A seat card is fine without it — it carries its own seat and the socket takes
 * it — so those go straight through and the show is not lost over a failed
 * fetch. Anything else CANNOT connect: the socket refuses a code with no seat,
 * correctly, and booting anyway would leave somebody watching "Reconnecting"
 * forever with no idea that the fault was two hops away. So it says so.
 */
async function unreachable(scanned: string): Promise<Admission> {
  if (carriesItsOwnSeat(scanned)) {
    // A card off a chair, and no door to ask: its seat is the real one.
    try { sessionStorage.setItem("cs_display", "seat"); } catch { /* private mode */ }
    done();
    return { token: scanned, spectator: false };
  }
  stop("entryOfflineTitle", "entryOffline", true);
  return await never();
}

/**
 * Send this phone somewhere else, because the hall is not admitting anyone.
 *
 * The address comes from the server on every refusal rather than being compiled
 * in: a venue changes where it wants people sent — a ticket page tonight, a
 * mailing list next month — and that must not need a client build.
 *
 * A word first, briefly. A screen that vanishes into another site with no
 * explanation reads as a hijacked link, which is exactly the wrong feeling for
 * somebody who just scanned a code in a foyer.
 */
async function leave(url?: string): Promise<never> {
  const target = typeof url === "string" && /^https?:\/\//i.test(url) ? url : null;
  say("message", t("entryClosedTitle"), t("entryClosedSub"));
  if (!target) {
    // Nowhere to send them. Better a plain "not now" than a blank screen.
    return await never();
  }
  await new Promise((r) => setTimeout(r, 1200));
  location.replace(target);
  return await never();
}

/** A promise that is never kept. The screen it leaves up is the answer. */
function never(): Promise<never> {
  return new Promise<never>(() => { /* deliberately */ });
}
